import type { ReactNode } from "react";
import { CalendarCheck } from "lucide-react";
import { ADVISORY_FORM_PATH, SOCIAL } from "@/config/constants";
import { WhatsAppIcon } from "@/components/icons/WhatsAppIcon";
import { formatEuro, usePricing } from "@/data/pricing";
import { useAppTranslation } from "@/i18n/useAppTranslation";
import { useLocale } from "@/i18n/useLocale";
import { trackEvent } from "@/utils/analytics";
import { ButtonLink, type ButtonSize, type ButtonVariant } from "./Button";

/** Price of the paid advisory, so every "book" button names what it costs. */
function useAdvisoryPrice(): string | null {
  const locale = useLocale();
  const pricing = usePricing();
  const item = pricing.services.items.find((s) => s.id === "asesoria");
  return item ? formatEuro(item.price, locale) : null;
}

/**
 * The paid Asesoría Estratégica: the second rung after the free audit.
 * Leads to the intake form; the calendar opens once it is sent.
 */
export function AuditButton({
  location,
  variant = "outline",
  size = "lg",
  label,
  className,
  withIcon = false,
  withPrice = true,
}: {
  location: string;
  variant?: ButtonVariant;
  size?: ButtonSize;
  label?: ReactNode;
  className?: string;
  withIcon?: boolean;
  /** Appends the advisory price to the default label. */
  withPrice?: boolean;
}) {
  const { t } = useAppTranslation("common");
  const price = useAdvisoryPrice();
  const text = label ?? (withPrice && price ? t("bookAuditPriced", { price }) : t("bookAudit"));
  return (
    <ButtonLink
      to={ADVISORY_FORM_PATH}
      variant={variant}
      size={size}
      className={className}
      arrow
      icon={withIcon ? <CalendarCheck aria-hidden="true" className="size-4 shrink-0" /> : undefined}
      data-tracked=""
      onClick={() => trackEvent("calendly_click", { location })}
    >
      {text}
    </ButtonLink>
  );
}

/** One line under the main call to action that lowers the stakes of asking. */
export function CtaReassurance({ className = "", tone = "light" }: { className?: string; tone?: "light" | "navy" }) {
  const { t } = useAppTranslation("common");
  return (
    <p className={`text-sm ${tone === "navy" ? "text-on-navy-muted" : "text-ink-muted"} ${className}`}>
      {t("auditReassurance")}
    </p>
  );
}

/** Opens a WhatsApp chat; `message` pre-fills the text (e.g. a service keyword). */
export function WhatsAppButton({
  location,
  variant = "outline",
  size = "lg",
  label,
  message,
  className,
  ariaLabel,
}: {
  location: string;
  variant?: ButtonVariant;
  size?: ButtonSize;
  label?: ReactNode;
  message?: string;
  className?: string;
  /** Accessible name when `label` is not plain text. */
  ariaLabel?: string;
}) {
  const { t } = useAppTranslation("common");
  const href = message
    ? `${SOCIAL.whatsappDirect}?text=${encodeURIComponent(message)}`
    : SOCIAL.whatsapp;
  const name = ariaLabel ?? (typeof label === "string" ? label : t("whatsapp"));
  return (
    <ButtonLink
      to={href}
      variant={variant}
      size={size}
      className={className}
      icon={<WhatsAppIcon className="size-4 shrink-0" />}
      aria-label={`${name} ${t("opensNewTab")}`}
      data-tracked=""
      onClick={() => trackEvent("whatsapp_click", { location })}
    >
      {label ?? t("whatsapp")}
    </ButtonLink>
  );
}

/** The free audit, requested on WhatsApp: the main call to action across the site. */
export function FreeAuditButton({
  location,
  variant = "primary",
  size = "lg",
  label,
  className,
}: {
  location: string;
  variant?: ButtonVariant;
  size?: ButtonSize;
  label?: ReactNode;
  className?: string;
}) {
  const { t } = useAppTranslation("common");
  const name = t("requestAuditCta");
  return (
    <WhatsAppButton
      location={location}
      variant={variant}
      size={size}
      className={className}
      label={label ?? name}
      ariaLabel={typeof label === "string" ? label : name}
      message={t("requestAuditMessage")}
    />
  );
}
